/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { Injectable, OnDestroy } from '@angular/core';
import * as uuid from 'uuid';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { updateApplicationPresence, updateLocalApplicationPresence } from '../+state/user.actions';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { debounceTime, filter } from 'rxjs/operators';
import { Topic } from '../model/multiple-subscriptions';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { selectPrimaryActiveClusterNameMultipleWebsocketConnection } from '../../call/+state/call.selectors';
import { updatePrimaryDataConnection } from '../../configuration/+state/configuration.actions';

@Injectable({
  providedIn: 'root'
})
export class PresenceService extends StompBaseConsumer implements OnDestroy {
    private topicSubscription: Subscription | undefined;
    private readonly receipt = uuid.v4();

    private primaryClusterName: string;

    constructor(protected store: Store, protected stompManager: StompManagerService) {
        super(stompManager, store);
            this.store.select(selectPrimaryActiveClusterNameMultipleWebsocketConnection).
                pipe(
                    filter((clusterName) => !!clusterName && clusterName !== this.primaryClusterName),
                    debounceTime(250)
                ).subscribe((clusterName) => {
                if (this.topicSubscription) {
                    console.log(`Unsubscribing from primary cluster ${this.primaryClusterName}`);
                    this.topicSubscription.unsubscribe();
                }
                this.primaryClusterName = clusterName;
                console.log(`Subscribing to primary cluster ${clusterName}`);
                this.topicSubscription = stompManager.subscribeConnectionToTopic(Topic.APPLICATION, this.receipt, this, clusterName, 'Primary').subscribe();
            });
    }

    protected getTopicHandlers() {
        return {
            [Topic.CALL_STATION_LOCAL]: this.handleCallStationLocal.bind(this)
        };
    }

    protected getTopics(): Topic[] {
        return [Topic.CALL_STATION_LOCAL];
    }

    handleCallStationLocal(message: any): void {
        console.info(`handleCallStationLocal topic ${Topic.CALL_STATION_LOCAL} message: ${message}`);
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Application presence topic ${topic} subscription success: ${receiptId}`);
        if(topic === Topic.APPLICATION) {
            this.store.dispatch(updateApplicationPresence({ loggedIn: true, loginTime: new Date().getTime() }));
            this.store.dispatch(updatePrimaryDataConnection({ clusterName: clusterName }));
        }
        if (topic === Topic.CALL_STATION_LOCAL) {
            this.store.dispatch(updateLocalApplicationPresence({ clusterName: clusterName, loggedIn: true, loginTime: new Date().getTime() }));
        }
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Application presence topic ${topic} subscription end: ${receiptId}`);
        if(topic === Topic.APPLICATION) {
            this.store.dispatch(updateApplicationPresence({ loggedIn: false }));
        }
        if (topic === Topic.CALL_STATION_LOCAL) {
            this.store.dispatch(updateLocalApplicationPresence({ clusterName: clusterName, loggedIn: false }));
        }
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Application presence topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    ngOnDestroy(): void {
        if (this.topicSubscription) {
            this.topicSubscription.unsubscribe();
        }
    }
}
